/**
 * The VWAP curve attach loop, extracted so it can be ARMED (HUDCHAT-26, owed to GK-bw's
 * VWAP-ATTACH land 5f167bfa, ruled R1667).
 *
 * ⛔ WHY IT MOVED OUT OF THE PANEL. GK-bw landed VWAP-ATTACH and named the hole in its own
 * receipt: mutants V1 (retry budget → 0) and V5 (the single-frame race restored — the STATED
 * CAUSE of the bug) BOTH SURVIVED, because the panel's only guard was a source text match for
 * `requestAnimationFrame(() => attempt(`, which the FIRST frame call satisfies. Delete the
 * retry and the text still matches. The registry half was armed; the retry half was not.
 *
 * ⭐ AND IT IS NOT THEORETICAL. HUDCHAT-26's served-page read (row b8f0e740) found the same
 * defect from the other end, before that receipt existed: the SAME bundle, two loads twenty
 * seconds apart — one showing `VWAP ERROR` on both charts, one showing no error and no curves.
 * A race is exactly what a first-frame-only attach produces, and it was on the owner's page.
 *
 * Logic inside a Svelte `$effect` cannot be driven by a unit test; a pure function taking its
 * scheduler as a parameter can. Behaviour is unchanged — the panel passes
 * `requestAnimationFrame` / `cancelAnimationFrame` and the same budget.
 */

/**
 * Frames to wait for the child chart to register before refusing.
 *
 * Exported as a VALUE, not left as a private literal, so an arm can assert it is at least one.
 * A text match for the constant's NAME survives setting it to zero; `>= 1` does not.
 */
export const VWAP_SYNC_MAX_RETRIES = 4;
/** Network-backed chart mounts may legitimately take seconds; host absence is bounded, not endless. */
export const VWAP_HOST_READY_TIMEOUT_MS = 30_000;

export interface AttachWithRetryOptions {
  /** One attempt. Returns true once the curves are attached. */
  attach: () => boolean;
  /** Extra frames to wait after the first attempt. Zero means one attempt and no retry. */
  maxRetries: number;
  schedule: (callback: () => void) => number;
  cancel: (handle: number) => void;
  /** Called once, with the total number of frames spent, when every attempt has failed. */
  onRefused: (framesSpent: number) => void;
}

/**
 * Attempt `attach` on the next frame, retrying up to `maxRetries` further frames.
 * Returns a stop function; after it runs, no further attempt or refusal occurs.
 */
export function attachWithRetry(options: AttachWithRetryOptions): () => void {
  const { attach, maxRetries, schedule, cancel, onRefused } = options;
  let frame: number | null = null;
  let stopped = false;

  const attempt = (retry: number): void => {
    if (stopped) return;
    frame = null;
    if (attach()) return;
    if (retry < maxRetries) {
      frame = schedule(() => attempt(retry + 1));
      return;
    }
    onRefused(retry + 1);
  };

  frame = schedule(() => attempt(0));
  return () => {
    stopped = true;
    if (frame !== null) cancel(frame);
    frame = null;
  };
}

export interface AttachWhenHostReadyOptions extends AttachWithRetryOptions {
  /** The existing armed retry implementation; injectable so the panel proves it is still fed. */
  retry?: (options: AttachWithRetryOptions) => () => void;
  /** Subscribe to the structural prerequisite; callback fires only for a positive chart-host box. */
  observeHost: (onReady: () => void) => () => void;
  hostTimeoutMs: number;
  scheduleTimeout: (callback: () => void, delayMs: number) => number;
  cancelTimeout: (handle: number) => void;
  /** Called once when the structural prerequisite never appears within the stated bound. */
  onHostRefused: (waitedMs: number) => void;
}

/**
 * Wait for host existence by signal, then retain the frame retry loop for every later prerequisite.
 * Frame count never spends the host deadline: a network round trip may outlive any number of frames.
 */
export function attachWhenHostReady(options: AttachWhenHostReadyOptions): () => void {
  const {
    retry = attachWithRetry,
    observeHost, hostTimeoutMs, scheduleTimeout, cancelTimeout, onHostRefused,
  } = options;
  if (!Number.isFinite(hostTimeoutMs) || hostTimeoutMs <= 0) {
    throw new RangeError(`VWAP chart host readiness: timeout must be positive; received ${hostTimeoutMs}`);
  }

  let stopped = false;
  let started = false;
  let stopObserving: (() => void) | null = null;
  let stopRetrying: (() => void) | null = null;
  let timeout: number | null = null;

  const start = (): void => {
    if (stopped || started) return;
    started = true;
    if (timeout !== null) cancelTimeout(timeout);
    timeout = null;
    stopObserving?.();
    stopObserving = null;
    stopRetrying = retry(options);
  };

  stopObserving = observeHost(start);
  // An injected observer may report an already-ready host synchronously before returning its stop.
  if (started) {
    stopObserving();
    stopObserving = null;
  } else {
    timeout = scheduleTimeout(() => {
      if (stopped || started) return;
      stopped = true;
      timeout = null;
      stopObserving?.();
      stopObserving = null;
      onHostRefused(hostTimeoutMs);
    }, hostTimeoutMs);
  }

  return () => {
    stopped = true;
    stopObserving?.();
    stopObserving = null;
    stopRetrying?.();
    stopRetrying = null;
    if (timeout !== null) cancelTimeout(timeout);
    timeout = null;
  };
}
